import { FC, useEffect, useMemo, useState } from 'react';
import { usePostsContainer } from 'components/posts/context';
import dayjs, { Dayjs } from 'dayjs';
import DateUtils from 'app/utils/date';
import { Switch } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import { usePostsLayoutState } from 'app/modules/posts/hooks/usePostsLayoutState';
import CalendarUtils from 'app/utils/calendar';
import PostsConfig from 'app/config/posts';
import { useEffectOnce } from 'utils/hooks/useEffectOnce';
import DatePickerDropdown from 'app/components/datepicker-dropdown';
import DatePickerHeaderCustomPeriod from 'app/components/datepicker-dropdown/custom-header/DatePickerHeaderCustomPeriod';
import DatePickerFooter from 'app/components/datepicker-dropdown/DatePickerFooter';
import DatePickerHeaderDefault from 'app/components/datepicker-dropdown/custom-header/DatePickerHeaderDefault';
import { getMaximumPostsFilterEndDate } from 'app/modules/posts/utils/getValidPostsFilterDateRange';
import useSetState from 'utils/hooks/useSetState';
import {
  isDateWithinRange,
  isDateRangeComplete,
} from 'app/components/datepicker-dropdown/utils';
import { getRangePlaceholderLabel } from 'app/components/datepicker-dropdown/utils/placeholder';

const MIN_DATE = dayjs()
  .subtract(PostsConfig.MIN_PREVIOUS_NUMBER_OF_YEARS, 'year')
  .startOf('year');

const MAX_MONTH_DATE_TO_SELECT = dayjs()
  .add(PostsConfig.MAX_NEXT_NUMBER_OF_YEARS, 'year')
  .endOf('year');

type Props = {
  startDate: Dayjs;
  endDate: Dayjs;
  onChange: ({
    startDate,
    endDate,
  }: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => void;
};

const PostsDatePicker: FC<Props> = ({ startDate, endDate, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useSetState<{
    startDate: boolean;
    endDate: boolean;
  }>({
    startDate: true,
    endDate: true,
  });
  const [startDateTemp, setStartDateTemp] = useState<Dayjs | null>(
    startDate ?? null,
  );
  const [endDateTemp, setEndDateTemp] = useState<Dayjs | null>(endDate ?? null);

  const postsContainer = usePostsContainer();
  const { t } = useTranslation();

  const {
    dateViewMode,
    setDateViewMode,
    calendarViewMode,
    setCalendarViewMode,
  } = usePostsLayoutState();

  const onCancelCustomPeriod = () => {
    setStartDateTemp(startDate);
    setEndDateTemp(endDate);
  };

  const onDateRangeSubmit = () => {
    if (isDateRangeComplete(startDateTemp, endDateTemp)) {
      if (calendarViewMode === 'week') {
        setCalendarViewMode('month');
      }

      onChange({
        startDate: startDateTemp ?? null,
        endDate: endDateTemp ?? null,
      });
      setIsOpen(false);
    }
  };

  const onDateSelect = (date?: Date) => {
    if (date) {
      if (calendarViewMode === 'week') {
        const { start, end } = CalendarUtils.getWeekRange(
          CalendarUtils.resolveWeekAnchor(DateUtils.toDateString(dayjs(date))),
        );

        onChange({ startDate: start, endDate: end });
        setIsOpen(false);
        return;
      }

      const startDate = dayjs(date);
      const endDate = dayjs(date);

      if (startDate && endDate) {
        onChange({
          startDate: startDate.startOf('M'),
          endDate: endDate.endOf('M'),
        });
        setIsOpen(false);
      }
    }
  };

  const onThreeMonthSelect = (date?: Date) => {
    if (date) {
      const { startDate: newStartDate, endDate: newEndDate } =
        DateUtils.getThreeMonthsRangeFromAnchor(dayjs(date));

      setStartDateTemp(newStartDate);
      setEndDateTemp(newEndDate);
      onChange({
        startDate: newStartDate,
        endDate: newEndDate,
      });
      setIsOpen(false);
    }
  };

  const onRangeSelect = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;

    setStartDateTemp(start ? dayjs(start) : null);
    setEndDateTemp(end ? dayjs(end) : null);
  };

  const label = useMemo(() => {
    if (dateViewMode === 'threeMonths' && startDate && endDate) {
      return `${startDate.format('MMM')} - ${endDate.format('MMM YYYY')}`;
    }

    if (dateViewMode === 'custom') {
      return getRangePlaceholderLabel({
        startDateTemp,
        endDateTemp,
      });
    }

    return DateUtils.toMonthYearString(startDate ?? null);
  }, [startDate, endDate, startDateTemp, endDateTemp, dateViewMode]);

  const selectedDate = useMemo(
    () => (dateViewMode === 'month' ? startDate ?? null : null),
    [startDate, dateViewMode],
  );

  const maxDate = useMemo(
    () =>
      startDateTemp
        ? getMaximumPostsFilterEndDate(startDateTemp)
        : MAX_MONTH_DATE_TO_SELECT,
    [startDateTemp],
  );

  const isIncomplete = useMemo(
    () => !isDateRangeComplete(startDateTemp, endDateTemp),
    [startDateTemp, endDateTemp],
  );

  useEffectOnce(() => {
    const { startDate: start, endDate: end } = postsContainer.filterState;

    if (DateUtils.isDateRangeMatchesMonth(start, end)) {
      setDateViewMode('month');
    } else if (DateUtils.isDateRangeMatchesThreeMonths(start, end)) {
      setDateViewMode('threeMonths');
    } else {
      setDateViewMode('custom');
    }
  });

  useEffect(() => {
    if (MIN_DATE && maxDate && startDate && endDate) {
      setError({
        startDate: startDate
          ? isDateWithinRange(startDate, MIN_DATE, dayjs(maxDate))
          : false,
        endDate: endDate
          ? isDateWithinRange(endDate, MIN_DATE, dayjs(maxDate))
          : false,
      });
    }
  }, [startDate, endDate, maxDate, setError]);

  useEffect(() => {
    if (!isOpen) {
      setStartDateTemp(startDate ?? null);
      setEndDateTemp(endDate ?? null);
    }
  }, [startDate, endDate, isOpen]);

  return (
    <DatePickerDropdown
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      label={label}
      contentClassname="tw-min-w-[300px]"
      selected={selectedDate?.toDate() ?? null}
      startDate={
        dateViewMode !== 'month' ? startDateTemp?.toDate() ?? null : null
      }
      endDate={dateViewMode !== 'month' ? endDateTemp?.toDate() ?? null : null}
      selectsRange={dateViewMode !== 'month'}
      showMonthYearPicker={dateViewMode !== 'custom'}
      dateFormat={dateViewMode !== 'custom' ? 'MM/yyyy' : undefined}
      onChange={(val) => {
        if (dateViewMode === 'custom') {
          onRangeSelect(val as [Date | null, Date | null]);
        } else if (dateViewMode === 'threeMonths') {
          const [date] = val as [Date | null, Date | null];
          onThreeMonthSelect(date ?? undefined);
        } else {
          onDateSelect(val as Date);
        }
      }}
      renderCustomHeader={(headerProps) => (
        <>
          <div className="-tw-mx-3 tw-mb-2 tw-border-b tw-border-grayscale-20 tw-px-3 tw-pb-3">
            <Switch.List>
              <Switch.Tab
                active={dateViewMode === 'month'}
                onClick={() => {
                  if (dateViewMode !== 'month') {
                    setDateViewMode('month');
                  }
                }}
                data-name="posts-date-picker_month-tab"
              >
                {t('oneMonth', { defaultValue: '1 month' })}
              </Switch.Tab>
              <Switch.Tab
                active={dateViewMode === 'threeMonths'}
                onClick={() => {
                  if (dateViewMode !== 'threeMonths') {
                    const { startDate: newStartDate, endDate: newEndDate } =
                      DateUtils.getThreeMonthsRangeFromAnchor(
                        startDate ?? dayjs(),
                      );

                    setDateViewMode('threeMonths');
                    setStartDateTemp(newStartDate);
                    setEndDateTemp(newEndDate);
                    onChange({
                      startDate: newStartDate,
                      endDate: newEndDate,
                    });
                  }
                }}
                data-name="posts-date-picker_three-months-tab"
              >
                {t('threeMonths', { defaultValue: '3 months' })}
              </Switch.Tab>
              <Switch.Tab
                active={dateViewMode === 'custom'}
                onClick={() => {
                  if (dateViewMode !== 'custom') {
                    setDateViewMode('custom');
                    setEndDateTemp(endDate);
                    setStartDateTemp(startDate);
                  }
                }}
                data-name="posts-date-picker_custom-tab"
              >
                {t('custom', { defaultValue: 'Custom' })}
              </Switch.Tab>
            </Switch.List>
          </div>
          {dateViewMode === 'custom' ? (
            <DatePickerHeaderCustomPeriod
              {...headerProps}
              minDate={MIN_DATE}
              maxDate={dayjs(maxDate)}
              error={error}
              startDate={startDateTemp ?? startDate}
              endDate={endDateTemp ?? endDate}
              onStartDateChange={(date) => setStartDateTemp(date)}
              onEndDateChange={(date) => setEndDateTemp(date)}
            />
          ) : (
            <DatePickerHeaderDefault
              {...headerProps}
              minDate={MIN_DATE}
              maxDate={MAX_MONTH_DATE_TO_SELECT}
            />
          )}
        </>
      )}
    >
      {dateViewMode === 'custom' && (
        <DatePickerFooter
          isCancelDisabled={
            startDate === startDateTemp && endDate === endDateTemp
          }
          disabled={isIncomplete}
          onDateRangeSubmit={onDateRangeSubmit}
          onRangeSelectCancel={onCancelCustomPeriod}
        />
      )}
    </DatePickerDropdown>
  );
};

export default PostsDatePicker;
